import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatDateTimeAU } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Activity } from 'lucide-react';

export default function ClientActivity() {
  const { client, clientId } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivity = async () => {
      if (!token || !clientId) return;
      try {
        const response = await api.getActivity(token, clientId);
        setActivity(response.data);
      } catch (error) {
        console.error('Failed to fetch activity:', error);
      }
      setLoading(false);
    };
    fetchActivity();
  }, [token, clientId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up" data-testid="activity-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/app/clients/${clientId}/overview`)}
            className="caffeine-btn-ghost mb-2"
            data-testid="back-to-overview-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to overview
          </Button>
          <h1 className="font-heading text-2xl text-[#2C2420]">Activity</h1>
          <p className="text-[#5D524D]">Recent activity for {client?.name} (last 30 days)</p>
        </div>
      </div>

      {/* Activity Feed */}
      {activity.length > 0 ? (
        <div className="caffeine-card">
          <div className="divide-y divide-[#E6E0D9]">
            {activity.map(item => (
              <div key={item.id} className="p-4 flex items-start gap-4" data-testid={`activity-${item.id}`}>
                <div className="w-10 h-10 bg-[#4A3B32]/10 rounded-full flex items-center justify-center text-[#4A3B32] text-sm font-medium flex-shrink-0">
                  {item.actor.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#2C2420]">
                    <span className="font-medium">{item.actor}</span>
                    {' '}<span className="text-[#8C8078]">{item.action.toLowerCase()}</span>{' '}
                    <span className="text-[#4A3B32] font-medium">{item.object}</span>
                  </p>
                  <p className="text-sm text-[#5D524D] mt-1">{item.outcome}</p>
                </div>
                <span className="text-xs text-[#8C8078] whitespace-nowrap">
                  {formatDateTimeAU(item.timestamp)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <Activity className="w-12 h-12" />
          <p className="text-lg font-medium text-[#5D524D]">No recent activity</p>
          <p className="text-sm">No activity recorded in the last 30 days</p>
        </div>
      )}
    </div>
  );
}
